import { StitcherSessionResponse } from '@paramount-streaming/blackbird';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IStitcherClipData } from 'tag/ts/tag/interfaces/snapshots/IStitcherClipData';
import { IStitcherSnapshot } from 'tag/ts/tag/interfaces/snapshots/IStitcherSnapshot';
import { IStitcherThumbnailDimension } from 'tag/ts/tag/interfaces/snapshots/IStitcherThumbnailDimension';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Stitcher snapshot. Facade over stitcher-related plugin data (sessionjson, metadata, etc.)
 */
export class StitcherSnapshot extends AbstractTagClass implements IStitcherSnapshot {

	/**
	 * @description Sessionjson plugin ID used to retrieve the plugin from the player adapter
	 */
	static readonly #SESSIONJSON_PLUGIN_ID = 'sessionjson';

	/**
	 * @description Metadata plugin ID used to retrieve the plugin from the player adapter
	 */
	static readonly #METADATA_PLUGIN_ID = 'metadata';

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'StitcherSnapshot'
			}
		);
	}

	/**
	 * @description Returns the sessionjson plugin instance from the player adapter
	 */
	get #sessionJsonPlugin() {
		return this?.tag?.controller?.playerManager?.player?.getPlugin?.( StitcherSnapshot.#SESSIONJSON_PLUGIN_ID ) as
			{
				getSession?: () => StitcherSessionResponse | null;
				getThumbnailDimensions?: () => IStitcherThumbnailDimension[] | undefined;
				getThumbnail?: ( position: number, height: number, width: number ) => string | undefined;
			} | undefined;
	}

	/**
	 * @description Returns stitcher session data from the sessionjson plugin
	 * @returns {StitcherSessionResponse | null | undefined} The session data, null if plugin is active but no session yet, undefined if plugin is unavailable
	 */
	get session(): StitcherSessionResponse | null | undefined {
		return this.#sessionJsonPlugin?.getSession?.();
	}

	/**
	 * @description Returns the currently playing clip data from the metadata plugin
	 * @returns {IStitcherClipData | null | undefined} The clip data, null if plugin is active but no clip yet, undefined if plugin is unavailable
	 */
	get clip(): IStitcherClipData | null | undefined {
		return ( this?.tag?.controller?.playerManager?.player?.getPlugin?.( StitcherSnapshot.#METADATA_PLUGIN_ID ) as
				{ getCurrentClipData?: () => IStitcherClipData | null } | undefined
		)?.getCurrentClipData?.();
	}

	/**
	 * @description Returns available thumbnail dimensions from the sessionjson plugin
	 * @returns {IStitcherThumbnailDimension[] | undefined} Array of available thumbnail sizes, undefined if plugin is unavailable
	 */
	get thumbnailDimensions(): IStitcherThumbnailDimension[] | undefined {
		return this.#sessionJsonPlugin?.getThumbnailDimensions?.();
	}

	/**
	 * @description Returns a thumbnail URL for a given playback position and size from the sessionjson plugin
	 * @param {number} position - Playback position in milliseconds
	 * @param {number} height - Desired thumbnail height in pixels
	 * @param {number} width - Desired thumbnail width in pixels
	 * @returns {string | undefined} Thumbnail image URL, undefined if unavailable
	 */
	getThumbnail = ( position: number, height: number, width: number ): string | undefined => {
		return this.#sessionJsonPlugin?.getThumbnail?.( position, height, width );
	};
}